import { useState, useEffect, useCallback, useRef } from 'react'
import type { Tab } from '../../../hooks/multiWindow/useTabManager'

/**
 * Shared hook for managing project sub-menu expand/collapse state.
 *
 * - Auto-expands the active project when navigating to it
 * - Collapses preview-only projects when clicking a different sidebar item
 *   (uses a DOM click listener on .standard-sidebar to bypass React memoization)
 *
 * @param lifeSpace - The life space ID (e.g. 'personal', 'work', 'home')
 * @param activeTab - The currently active tab
 * @param tabs - All visible tabs
 */
export function useProjectExpand(lifeSpace: string, activeTab?: Tab, tabs: Tab[] = []) {
  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set())
  const tabsRef = useRef(tabs)
  tabsRef.current = tabs
  const activeTabRef = useRef(activeTab)
  activeTabRef.current = activeTab

  const projectPathPattern = new RegExp(`/home/${lifeSpace}/project/([^/?]+)`)

  // Suppresses auto-expand for one cycle after the click handler collapses a project.
  // Without this, the auto-expand useEffect (which still sees the OLD activeTab.path)
  // would immediately re-add the project that was just collapsed, causing a visual glitch
  // where items reappear briefly before collapsing again.
  const suppressAutoExpandRef = useRef(false)

  // Auto-expand project when navigating to it (keyboard nav, back/forward, etc.)
  useEffect(() => {
    if (suppressAutoExpandRef.current) {
      suppressAutoExpandRef.current = false
      return
    }
    if (activeTab?.path) {
      const match = activeTab.path.match(projectPathPattern)
      if (match) {
        setExpandedProjects(prev => {
          if (prev.has(match[1])) return prev
          return new Set(prev).add(match[1])
        })
      }
    }
  }, [activeTab?.path])

  // Collapse preview-only projects when clicking any other sidebar item.
  // Also expands the clicked project in the same state update to avoid flicker.
  // Uses a DOM click listener — bypasses React.memo/useMemo memoization.
  useEffect(() => {
    const sidebar = document.querySelector('.standard-sidebar')
    if (!sidebar) return

    const handleSidebarClick = (e: Event) => {
      // Skip clicks on the expand chevron — its React onClick handler already
      // calls toggleProjectExpand. The native DOM listener fires before React's
      // synthetic handler, so without this guard both would run: DOM expands,
      // then React toggles (removes) → double-toggle leaves project collapsed.
      if ((e.target as HTMLElement).closest('.sidebar-item__sub-chevron')) return

      const target = (e.target as HTMLElement).closest('[data-path]')
      if (!target) return
      const clickedPath = target.getAttribute('data-path')
      if (!clickedPath) return

      const targetMatch = clickedPath.match(projectPathPattern)
      const targetProjectId = targetMatch?.[1]

      setExpandedProjects(prev => {
        let next = prev

        // Collapse preview-only projects that aren't the click target
        for (const expandedId of prev) {
          if (expandedId === targetProjectId) continue
          const projPath = `/home/${lifeSpace}/project/${expandedId}`
          const hasPermanentTab = tabsRef.current.some(t => t.path === projPath && !t.isPreview)
          if (!hasPermanentTab) {
            if (next === prev) next = new Set(prev)
            next.delete(expandedId)
          }
        }

        // Expand clicked project — but NOT if it's already the active project,
        // because ProjectSidebarItem.handleProjectClick already calls toggleProjectExpand
        // and the DOM listener fires before React's onClick (would cause double-toggle).
        const activeMatch = activeTabRef.current?.path?.match(projectPathPattern)
        const isAlreadyActive = activeMatch?.[1] === targetProjectId
        if (targetProjectId && !isAlreadyActive && !(next === prev ? prev : next).has(targetProjectId)) {
          if (next === prev) next = new Set(prev)
          next.add(targetProjectId)
        }

        // Suppress auto-expand so the useEffect doesn't re-add the old project
        if (next !== prev) {
          suppressAutoExpandRef.current = true
        }

        return next
      })
    }

    sidebar.addEventListener('click', handleSidebarClick)
    return () => sidebar.removeEventListener('click', handleSidebarClick)
  }, [])

  const toggleProjectExpand = useCallback((projectId: string) => {
    setExpandedProjects(prev => {
      const next = new Set(prev)
      if (next.has(projectId)) { next.delete(projectId) } else { next.add(projectId) }
      return next
    })
  }, [])

  return { expandedProjects, toggleProjectExpand }
}
